import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { cn } from '@/lib/cn';

export const TooltipProvider = RadixTooltip.Provider;
export const TooltipRoot = RadixTooltip.Root;
export const TooltipTrigger = RadixTooltip.Trigger;

interface TooltipContentProps extends ComponentPropsWithoutRef<typeof RadixTooltip.Content> {
  children: ReactNode;
}

export const TooltipContent = forwardRef<
  ElementRef<typeof RadixTooltip.Content>,
  TooltipContentProps
>(function TooltipContent({ className, sideOffset = 6, children, ...rest }, ref) {
  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-overlay rounded-md bg-t1 px-2 py-1 text-2xs font-medium text-canvas shadow-card',
          'ease-out-strong data-[state=delayed-open]:duration-tooltip',
          'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0',
          'motion-reduce:animate-none',
          className,
        )}
        {...rest}
      >
        {children}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
});

/**
 * Convenience wrapper: <Tooltip label="…"><Trigger /></Tooltip>
 * Useful when you don't need to control open state manually.
 */
export interface TooltipProps {
  label: ReactNode;
  children: ReactNode;
  side?: ComponentPropsWithoutRef<typeof RadixTooltip.Content>['side'];
  delayDuration?: number;
}

export function Tooltip({ label, children, side = 'top', delayDuration = 300 }: TooltipProps) {
  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <TooltipContent side={side}>{label}</TooltipContent>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
